import { Modals } from '@/containers/Modals/all-modals';
import { useAppDispatch } from '@/lib/redux/store';
import { defaultTextEditorFont } from '@/shared/utils/text-editor';
import { useCallback, useState } from 'react';
import { addOpenModal } from '../../Modals/redux/modals.slice';
import { ViewTrackedLinksModalParams } from '../../Modals/types';
import { TextEditorFont } from '../types';
import { checkIsFontSupportingOperation } from '../utils';

const useToolbarOperations = () => {
  const dispatch = useAppDispatch();
  const [isBold, setIsBold] = useState(false);
  const [isItalics, setIsItalics] = useState(false);
  const [shouldDoOperation, setShouldDoOperation] = useState(false);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [selectedFont, setSelectedFont] = useState<TextEditorFont>(defaultTextEditorFont);

  const isBoldItalicsSupported = checkIsFontSupportingOperation(
    selectedFont.name,
    'boldItalics'
  );
  const isBoldSupported = checkIsFontSupportingOperation(selectedFont.name, 'bold');
  const isItalicsSupported = checkIsFontSupportingOperation(selectedFont.name, 'italics');

  const isBoldButtonEnabled =
    isBoldItalicsSupported || checkIsFontSupportingOperation(selectedFont.name, 'bold');
  const isItalicsButtonEnabled =
    isBoldItalicsSupported ||
    checkIsFontSupportingOperation(selectedFont.name, 'italics');

  const onToggleBoldOperation = useCallback(() => {
    if (!isBoldButtonEnabled) {
      return;
    }

    // Turning off
    if (isBold) {
      if (isBoldItalicsSupported && !isItalicsSupported) {
        setIsItalics(false);
      }
    } else {
      // Turning on
      if (isBoldItalicsSupported && !isBoldSupported) {
        setIsItalics(true);
      }
    }

    setIsBold(!isBold);
    setShouldDoOperation(true);
  }, [
    isBold,
    isBoldButtonEnabled,
    isBoldItalicsSupported,
    isBoldSupported,
    isItalicsSupported,
  ]);

  const onToggleItalicsOperation = useCallback(() => {
    if (!isItalicsButtonEnabled) {
      return;
    }

    // Turning off
    if (isItalics) {
      if (isBoldItalicsSupported && !isBoldSupported) {
        setIsBold(false);
      }
    } else {
      // Turning on
      if (isBoldItalicsSupported && !isItalicsSupported) {
        setIsBold(true);
      }
    }

    setIsItalics(!isItalics);
    setShouldDoOperation(true);
  }, [
    isBoldItalicsSupported,
    isItalics,
    isItalicsSupported,
    isBoldSupported,
    isItalicsButtonEnabled,
  ]);

  const onChangeFontOperation = (font: TextEditorFont) => {
    setSelectedFont(font);
    setIsBold(false);
    setIsItalics(false);
    setShouldDoOperation(true);
  };

  const onAddLinks = () => {
    dispatch(
      addOpenModal({
        modalName: Modals.ViewTrackedLinksModal,
        modalParams: JSON.stringify({
          allowInsertion: true,
        } as ViewTrackedLinksModalParams),
      })
    );
  };

  return {
    isBold,
    isItalics,
    canUndo,
    canRedo,
    selectedFont,
    isItalicsSupported,
    isBoldSupported,
    isBoldButtonEnabled,
    isItalicsButtonEnabled,
    shouldDoOperation,
    setIsBold,
    setIsItalics,
    setShouldDoOperation,
    setCanUndo,
    setCanRedo,
    setSelectedFont,
    onToggleBoldOperation,
    onToggleItalicsOperation,
    onChangeFontOperation,
    onAddLinks,
  };
};

export default useToolbarOperations;
